import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_INFO_BG, COLOR_INFO_TEXT } from 'theme/colors';
import { CAS_PANEL_COLORS } from 'theme/sourcePanelColors';
import { renderSafeHtml } from '../../utils/renderHtml';
import LgmButton from '../LgmButton';
import ExpandableFieldList from './ExpandableFieldList';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import {
  CasSourceRow,
  CasReviewQueueEntry,
  CasReviewCandidate,
} from '../../services/api/domains/products/types';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// `isHtml`: CAS-source names can carry markup (e.g. <span class="text-
// smallcaps">, <em>) — render it rather than showing the raw tags as
// literal text.
const FieldRow: React.FC<{
  label: string;
  value: string;
  isHtml?: boolean;
}> = ({ label, value, isHtml = false }) => (
  <div
    style={{
      display: 'flex',
      gap: '8px',
      padding: '4px 0',
      fontSize: '14px',
      lineHeight: '1.4',
    }}>
    <span
      style={{
        minWidth: '140px',
        fontWeight: 600,
        color: GRAY_500,
        flexShrink: 0,
      }}>
      {label}
    </span>
    {isHtml ? (
      // renderSafeHtml's DOMPurify profile permits block-level tags (e.g.
      // <div>) — a <span> wrapper would nest block content inside inline,
      // which is invalid HTML. Both are flex items here, so div vs. span
      // doesn't change layout.
      <div style={{ color: GRAY_900, wordBreak: 'break-word' }}>
        {renderSafeHtml(value)}
      </div>
    ) : (
      <span style={{ color: GRAY_900, wordBreak: 'break-word' }}>{value}</span>
    )}
  </div>
);

const SOURCE_LABELS: Record<string, string> = {
  common_chemistry: 'Common Chemistry',
  pubchem: 'PubChem',
  fda_gsrs: 'FDA GSRS',
};

const sourceLabel = (source: string) => SOURCE_LABELS[source] ?? source;

// `sources_involved` is stored as a comma-separated string (e.g.
// "pubchem,fda_gsrs") — map each token through sourceLabel individually
// rather than treating the whole string as one source key.
const sourceLabelList = (sourcesInvolved: string) => {
  const labels = sourcesInvolved
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(sourceLabel);
  return labels.length > 0 ? labels.join(', ') : 'an unknown source';
};

/**
 * One confirmed-source block: header badge, CAS number + match_basis, and a
 * "more details" accordion for structural fields. Mirrors the original
 * single-source layout so all sources render consistently.
 */
const SourceBlock: React.FC<{
  label: string;
  name?: string | null;
  casNumber?: string | null;
  matchBasis?: string | null;
  synonyms?: string[];
  molecularFormula?: string | null;
  molecularMass?: string | null;
  inchi?: string | null;
  inchiKey?: string | null;
  smile?: string | null;
  canonicalSmile?: string | null;
  replacedRns?: string[];
  uri?: string | null;
}> = ({
  label,
  name,
  casNumber,
  matchBasis,
  synonyms,
  molecularFormula,
  molecularMass,
  inchi,
  inchiKey,
  smile,
  canonicalSmile,
  replacedRns,
  uri,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const replacedRnsValue =
    replacedRns && replacedRns.length > 0 ? replacedRns.join(', ') : '—';

  const hasSecondary = !!(
    molecularFormula ||
    molecularMass ||
    inchi ||
    inchiKey ||
    smile ||
    canonicalSmile ||
    uri ||
    (replacedRns && replacedRns.length > 0)
  );

  return (
    <div
      style={{
        margin: '8px 0',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#eff6ff',
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: COLOR_INFO_BG,
          borderBottom: '1px solid #bfdbfe',
        }}>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#1d4ed8',
          }}>
          {label}
        </span>
        {matchBasis && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: COLOR_INFO_TEXT,
              backgroundColor: '#93c5fd',
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
            {matchBasis.replace('_', ' ')}
          </span>
        )}
      </div>

      <div style={{ padding: '8px 12px 4px 12px' }}>
        {name && <FieldRow label="CAS Name" value={name} isHtml />}
        {casNumber && <FieldRow label="CAS Number" value={casNumber} />}
        {synonyms && (
          <ExpandableFieldList label="Synonyms" items={synonyms} isHtml />
        )}
      </div>

      {hasSecondary && (
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(prev => !prev)}
          slotProps={{ transition: { unmountOnExit: false } }}
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
          style={{ backgroundColor: 'transparent' }}>
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                style={{ color: COLOR_INFO_TEXT, fontSize: '18px' }}
              />
            }
            style={{
              minHeight: '32px',
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: COLOR_INFO_TEXT,
              backgroundColor: 'transparent',
            }}>
            More details
          </AccordionSummary>
          <AccordionDetails
            style={{
              padding: '0 12px 8px 12px',
              backgroundColor: 'transparent',
            }}>
            {molecularFormula && (
              <FieldRow label="Molecular Formula" value={molecularFormula} />
            )}
            {molecularMass && (
              <FieldRow label="Molecular Mass" value={molecularMass} />
            )}
            {inchi && <FieldRow label="InChI" value={inchi} />}
            {inchiKey && <FieldRow label="InChI Key" value={inchiKey} />}
            {smile && <FieldRow label="SMILES" value={smile} />}
            {canonicalSmile && (
              <FieldRow label="Canonical SMILES" value={canonicalSmile} />
            )}
            {replacedRns && replacedRns.length > 0 && (
              <FieldRow label="Replaced RNs" value={replacedRnsValue} />
            )}
            {uri && <FieldRow label="URI" value={uri} />}
          </AccordionDetails>
        </Accordion>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Candidates section — quarantined results awaiting human confirmation (AD-8)
// ---------------------------------------------------------------------------

const CandidateRow: React.FC<{
  candidate: CasReviewCandidate;
  onChoose: () => void;
  disabled: boolean;
  isAdmin: boolean;
}> = ({ candidate, onChoose, disabled, isAdmin }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      padding: '6px 0',
      borderBottom: '1px solid #fde68a',
      fontSize: '13px',
    }}>
    <span style={{ color: GRAY_900 }}>
      {sourceLabel(candidate.source)} — {candidate.cas_number ?? 'unknown CAS'}
      {candidate.primary_name ? ` (${candidate.primary_name})` : ''}
    </span>
    <LgmButton
      variant="flat"
      disabled={disabled || !candidate.cas_number || !isAdmin}
      onClick={onChoose}
      title={
        !isAdmin
          ? 'Admin only'
          : candidate.cas_number
            ? undefined
            : 'This candidate has no CAS number — cannot be applied'
      }
      style={{ fontSize: '12px', padding: '4px 8px' }}>
      Choose this substance
    </LgmButton>
  </div>
);

// ---------------------------------------------------------------------------
// CASRegistryPanel
// ---------------------------------------------------------------------------

interface CASRegistryPanelProps {
  productId: number;
  /** RBAC-1 (issue #662, CQR-027): confirm/reject narrowed from
   * `products:write` to `quarantine-queue:write`, admin-only by omission --
   * an editor calling either now gets a 403. This panel's own write actions
   * ("Choose this substance" / "Dismiss") must gate on the same admin-only
   * check the server enforces, not render an enabled control that 403s. */
  isAdmin: boolean;
  isCasVerified: boolean;
  casNumber?: string | null;
  synonyms?: string[];
  molecularFormula?: string | null;
  molecularMass?: string | null;
  inchi?: string | null;
  inchiKey?: string | null;
  smile?: string | null;
  canonicalSmile?: string | null;
  replacedRns?: string[];
  uri?: string | null;
  casSources?: CasSourceRow[];
  casCandidates?: CasReviewQueueEntry[];
  // Per-source CAS official names (issue #585 Task 9.1) — sourced from
  // Product_Names, not casSources (CasSourceRow has no name field).
  casOfficialCommonChemistry?: string | null;
  casOfficialPubchem?: string | null;
  casOfficialFdaGsrs?: string | null;
}

/**
 * Read-only, multi-source CAS panel for the Product modal (issue #350, FR-7).
 * Renders one block per confirming source — Common Chemistry always uses the
 * flat legacy fields (its confirmations pre-date `Product_Cas_Source_Data`
 * and aren't backfilled for every product); PubChem/FDA GSRS use `casSources`
 * rows. All rendered sources agree on the same CAS by construction (AD-10) —
 * a disagreeing result is blocked at write and never appears here.
 *
 * Renders nothing when neither a verified CAS nor any pending candidate
 * exists (mirrors the original all-or-nothing gate, extended to candidates).
 */
const CASRegistryPanel: React.FC<CASRegistryPanelProps> = ({
  productId,
  isAdmin,
  isCasVerified,
  casNumber,
  synonyms,
  molecularFormula,
  molecularMass,
  inchi,
  inchiKey,
  smile,
  canonicalSmile,
  replacedRns,
  uri,
  casSources,
  casCandidates,
  casOfficialCommonChemistry,
  casOfficialPubchem,
  casOfficialFdaGsrs,
}) => {
  const api = useApi();
  const productService = React.useMemo(() => new ProductService(api), [api]);
  const [candidates, setCandidates] = React.useState(casCandidates ?? []);
  const [pendingReviewId, setPendingReviewId] = React.useState<number | null>(
    null
  );
  const [actionError, setActionError] = React.useState<string | null>(null);
  // Parent props (isCasVerified/casSources) only refresh on the next full
  // product fetch. Without this, confirming the candidate that establishes
  // the very first CAS would empty `candidates` while `isCasVerified` is
  // still stale-false, and the panel would vanish (Copilot review, PR #499).
  // Tracked locally so the panel keeps showing what was just confirmed.
  const [localEstablished, setLocalEstablished] = React.useState<{
    cas_number: string;
    source: string;
  } | null>(null);

  React.useEffect(() => {
    setCandidates(casCandidates ?? []);
  }, [casCandidates]);

  // A ProductModal instance can be reused to view a different product
  // without unmounting — reset all locally-tracked state on productId change
  // so a prior product's optimistic confirmation or in-flight action doesn't
  // leak into the next one (Copilot review, PR #499).
  React.useEffect(() => {
    setPendingReviewId(null);
    setActionError(null);
    setLocalEstablished(null);
  }, [productId]);

  const effectiveIsCasVerified = isCasVerified || Boolean(localEstablished);
  if (!effectiveIsCasVerified && candidates.length === 0) return null;

  // is_cas_verified can be set by the alternative-source orchestrator too,
  // not only by a Common Chemistry confirmation — deriving the Common
  // Chemistry contribution from isCasVerified over-counts sources and can
  // render an empty/incorrect "Common Chemistry" block for an alt-source-only
  // confirmation. Common Chemistry-specific fields only ever get populated
  // when Common Chemistry itself confirmed, so gate on those instead.
  const hasCommonChemistry = Boolean(
    uri ||
    (synonyms && synonyms.length > 0) ||
    molecularFormula ||
    inchi ||
    inchiKey ||
    smile ||
    canonicalSmile ||
    (replacedRns && replacedRns.length > 0)
  );

  const alternativeSources = (casSources ?? []).filter(
    s => s.source !== 'common_chemistry'
  );
  const localSourceAlreadyListed = localEstablished
    ? alternativeSources.some(s => s.source === localEstablished.source)
    : false;
  const confirmedCount =
    (hasCommonChemistry ? 1 : 0) +
    alternativeSources.length +
    (localEstablished && !localSourceAlreadyListed ? 1 : 0);

  const handleChoose = async (
    reviewId: number,
    candidate: CasReviewCandidate
  ) => {
    setActionError(null);
    setPendingReviewId(reviewId);
    try {
      await productService.confirmCasCandidate(productId, reviewId, candidate);
      setCandidates(prev => prev.filter(c => c.review_id !== reviewId));
      if (candidate.cas_number) {
        setLocalEstablished({
          cas_number: candidate.cas_number,
          source: candidate.source,
        });
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to confirm candidate'
      );
    } finally {
      setPendingReviewId(null);
    }
  };

  const handleDismiss = async (reviewId: number) => {
    setActionError(null);
    setPendingReviewId(reviewId);
    try {
      await productService.rejectCasCandidate(productId, reviewId);
      setCandidates(prev => prev.filter(c => c.review_id !== reviewId));
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to dismiss candidate'
      );
    } finally {
      setPendingReviewId(null);
    }
  };

  return (
    <div
      data-testid="cas-registry-panel"
      style={{
        margin: '20px 0 8px 0',
        border: `1px solid ${CAS_PANEL_COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: CAS_PANEL_COLORS.background,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: CAS_PANEL_COLORS.headerBackground,
          borderBottom: `1px solid ${CAS_PANEL_COLORS.border}`,
        }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: CAS_PANEL_COLORS.badgeText,
            backgroundColor: CAS_PANEL_COLORS.badgeBackground,
            padding: '2px 6px',
            borderRadius: '4px',
          }}>
          CAS Sources
        </span>
        {effectiveIsCasVerified && (
          <span style={{ fontSize: '12px', color: GRAY_500 }}>
            {casNumber ?? localEstablished?.cas_number} — confirmed by{' '}
            {confirmedCount} source
            {confirmedCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div style={{ padding: '8px 12px 4px 12px' }}>
        {hasCommonChemistry && (
          <SourceBlock
            label={sourceLabel('common_chemistry')}
            name={casOfficialCommonChemistry}
            casNumber={casNumber}
            synonyms={synonyms ?? []}
            molecularFormula={molecularFormula}
            molecularMass={molecularMass}
            inchi={inchi}
            inchiKey={inchiKey}
            smile={smile}
            canonicalSmile={canonicalSmile}
            replacedRns={replacedRns}
            uri={uri}
          />
        )}

        {alternativeSources.map(s => (
          <SourceBlock
            key={s.source}
            label={sourceLabel(s.source)}
            name={
              s.source === 'pubchem'
                ? casOfficialPubchem
                : s.source === 'fda_gsrs'
                  ? casOfficialFdaGsrs
                  : undefined
            }
            casNumber={s.cas_number}
            matchBasis={s.match_basis}
            synonyms={s.synonyms}
            molecularFormula={s.molecular_formula}
            molecularMass={s.molecular_weight}
            inchi={s.inchi}
            inchiKey={s.inchi_key}
            smile={s.smiles}
          />
        ))}

        {localEstablished && !localSourceAlreadyListed && (
          <SourceBlock
            label={sourceLabel(localEstablished.source)}
            casNumber={localEstablished.cas_number}
            matchBasis="human_confirmed"
          />
        )}

        {candidates.length > 0 && (
          <div
            style={{
              margin: '8px 0',
              border: '1px solid #fde68a',
              borderRadius: '6px',
              overflow: 'hidden',
              backgroundColor: '#fffbeb',
            }}>
            <div
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#92400e',
                borderBottom: '1px solid #fde68a',
              }}>
              Suggested — needs confirmation
            </div>
            <div style={{ padding: '4px 12px' }}>
              {candidates.map(entry =>
                entry.reason === 'ambiguous_name' ? (
                  (entry.blocked_payload?.candidates ?? []).map((c, i) => (
                    <CandidateRow
                      key={`${entry.review_id}-${i}`}
                      candidate={c}
                      disabled={pendingReviewId === entry.review_id}
                      isAdmin={isAdmin}
                      onChoose={() => handleChoose(entry.review_id, c)}
                    />
                  ))
                ) : (
                  <div
                    key={entry.review_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: '6px 0',
                      borderBottom: '1px solid #fde68a',
                      fontSize: '13px',
                    }}>
                    <span style={{ color: GRAY_900 }}>
                      Unconfirmed candidate from{' '}
                      {sourceLabelList(entry.sources_involved ?? '')} — no name
                      match, cannot be applied automatically
                    </span>
                    <LgmButton
                      variant="flat"
                      disabled={pendingReviewId === entry.review_id || !isAdmin}
                      onClick={() => handleDismiss(entry.review_id)}
                      title={isAdmin ? undefined : 'Admin only'}
                      style={{ fontSize: '12px', padding: '4px 8px' }}>
                      Dismiss
                    </LgmButton>
                  </div>
                )
              )}
            </div>
            {actionError && (
              <div
                style={{
                  padding: '4px 12px 8px 12px',
                  fontSize: '12px',
                  color: '#b91c1c',
                }}>
                {actionError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CASRegistryPanel;
